/**
 * V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29
 *
 * Hook qui contrôle la visibilité du SetupWizard par space.
 *
 * Comportement :
 * - Persisté dans localStorage avec clé `raiso_onboarding_visible_<spaceId>`.
 * - Default : `true` (affiché) — pour ne pas casser le 1er setup d'un nouveau space.
 * - L'utilisateur peut le masquer/réafficher via le toggle dans Settings.
 * - Le caller PostGalleryGrid combine cette préférence avec la présence de posts :
 *   le wizard est masqué automatiquement quand des posts existent, MÊME si la
 *   préférence est `true` (cf shouldShowWizard).
 *
 * Pourquoi pas un context ? Le state est per-space et purement local ; pas besoin
 * de provider — un hook avec localStorage suffit.
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'raiso_onboarding_visible_';

function getStorageKey(spaceId: string | undefined | null): string | null {
  if (!spaceId) return null;
  return `${STORAGE_KEY_PREFIX}${spaceId}`;
}

function readPreference(spaceId: string | undefined | null): boolean {
  const key = getStorageKey(spaceId);
  if (!key) return true;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return true; // default: visible
    return raw === 'true';
  } catch {
    return true;
  }
}

function writePreference(spaceId: string | undefined | null, visible: boolean): void {
  const key = getStorageKey(spaceId);
  if (!key) return;
  try {
    localStorage.setItem(key, String(visible));
  } catch {
    // localStorage quota or disabled — silent fail, behavior degrades gracefully
  }
}

export interface UseOnboardingPreferenceResult {
  /** True si l'utilisateur souhaite voir le wizard d'onboarding pour ce space */
  visible: boolean;
  /** Toggle / set explicitly (e.g. depuis le bouton "Commencer à générer" du wizard ou un toggle Settings) */
  setVisible: (next: boolean) => void;
}

export function useOnboardingPreference(spaceId: string | undefined | null): UseOnboardingPreferenceResult {
  const [visible, setVisibleState] = useState<boolean>(() => readPreference(spaceId));

  // Re-lire la préférence si le space change (ex: switch space dans agence).
  useEffect(() => {
    setVisibleState(readPreference(spaceId));
  }, [spaceId]);

  // Sync entre tabs/windows (storage event fires sur les autres tabs).
  useEffect(() => {
    const key = getStorageKey(spaceId);
    if (!key) return;
    const handler = (e: StorageEvent) => {
      if (e.key === key) {
        setVisibleState(readPreference(spaceId));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [spaceId]);

  const setVisible = useCallback(
    (next: boolean) => {
      writePreference(spaceId, next);
      setVisibleState(next);
    },
    [spaceId]
  );

  return { visible, setVisible };
}
